import type {
  CreateExecutionContext,
  CreateInput,
  CreateInterceptionResult,
  JobOptions,
  Plugin,
  RuntimeUploadScopeOptions,
  ServicePluginContext,
  Tool,
} from "@brains/plugins";
import { createPendingEntity, ServicePlugin } from "@brains/plugins";
import { slugify } from "@brains/utils/string-utils";
import { z } from "@brains/utils/zod";
import {
  documentAdapter,
  documentSchema,
  type DocumentAdapter,
  type DocumentEntity,
} from "@brains/document";
import {
  DocumentGenerationJobHandler,
  documentGenerationJobSchema,
  getDocumentId,
} from "./handlers/documentGenerationHandler";
import packageJson from "../package.json";

const PENDING_PDF_DATA_URL = `data:application/pdf;base64,${Buffer.from(
  "%PDF-1.4\n% Pending document placeholder\n%%EOF\n",
).toString("base64")}`;

type DocumentPluginConfig = Record<string, never>;
type DocumentPluginConfigInput = Record<string, unknown>;

function createAttributionJobOptions(
  executionContext: CreateExecutionContext,
): JobOptions {
  return {
    source: "document",
    metadata: {
      operationType: "content_operations",
      interfaceType: executionContext.interfaceType,
      requestedByActor: executionContext.actor,
      ...(executionContext.actor.kind === "user"
        ? { requestedByUserId: executionContext.actor.userId }
        : {}),
      requestedByInterface: executionContext.interfaceType,
    },
  };
}

const documentPluginConfigSchema: z.ZodType<
  DocumentPluginConfig,
  DocumentPluginConfigInput
> = z
  .object({})
  .catchall(z.unknown())
  .transform((): DocumentPluginConfig => ({}));

const webChatUploadsScope: RuntimeUploadScopeOptions & {
  refKind: "upload";
} = {
  namespace: "upload",
  refKind: "upload",
  routePath: "/api/chat/uploads",
};

function getUploadTitle(input: CreateInput, filename: string): string {
  const title = input.title?.trim();
  if (title) return title;
  const withoutExt = filename.replace(/\.[^.]+$/, "").trim();
  return withoutExt || filename;
}

function toDataUrl(mediaType: string, content: Buffer): string {
  return `data:${mediaType};base64,${content.toString("base64")}`;
}

function buildUploadedDocumentAttachment(input: {
  entityId: string;
  filename: string;
}): {
  mediaType: "application/pdf";
  url: string;
  downloadUrl: string;
  filename: string;
  source: {
    entityType: "document";
    entityId: string;
    attachmentType: "uploaded";
  };
} {
  const encodedId = encodeURIComponent(input.entityId);
  return {
    mediaType: "application/pdf",
    url: `/api/chat/attachments/document?id=${encodedId}`,
    downloadUrl: `/api/chat/attachments/document?id=${encodedId}&download=1`,
    filename: input.filename,
    source: {
      entityType: "document",
      entityId: input.entityId,
      attachmentType: "uploaded",
    },
  };
}

export class DocumentPlugin extends ServicePlugin<
  DocumentPluginConfig,
  DocumentPluginConfigInput
> {
  readonly entityType: typeof documentAdapter.entityType =
    documentAdapter.entityType;
  readonly schema: typeof documentSchema = documentSchema;
  readonly adapter: DocumentAdapter = documentAdapter;
  private pluginContext: ServicePluginContext | undefined;

  constructor() {
    super("document", packageJson, {}, documentPluginConfigSchema);
  }

  protected override async onRegister(
    context: ServicePluginContext,
  ): Promise<void> {
    this.pluginContext = context;
    context.entities.register(this.entityType, this.schema, this.adapter, {
      embeddable: false,
      projectionSource: false,
      projectionSourceRole: "excluded",
    });
    context.entities.registerCreateInterceptor(
      this.entityType,
      (input, executionContext) =>
        this.interceptCreate(input, executionContext),
    );
    context.entities.registerUploadSaveHandler({
      entityType: this.entityType,
      mediaTypes: ["application/pdf"],
      handler: async (input, executionContext) => {
        const interception = await this.promoteUpload(
          {
            entityType: this.entityType,
            ...(input.title !== undefined ? { title: input.title } : {}),
            ...(input.visibility !== undefined
              ? { visibility: input.visibility }
              : {}),
            from: input.upload,
          },
          context,
          executionContext,
        );
        return interception.kind === "handled"
          ? interception.result
          : { success: false, error: "Document upload save was not handled" };
      },
    });
    context.jobs.registerHandler(
      "generate",
      new DocumentGenerationJobHandler(
        this.logger.child("DocumentGenerationJobHandler"),
        context,
      ),
    );
  }

  private async interceptCreate(
    input: CreateInput,
    executionContext: CreateExecutionContext,
  ): Promise<CreateInterceptionResult> {
    const context = this.pluginContext;
    if (input.from?.kind === webChatUploadsScope.refKind) {
      if (!context) {
        return {
          kind: "handled",
          result: { success: false, error: "Plugin context not initialized" },
        };
      }
      return this.promoteUpload(input, context, executionContext);
    }

    if (input.from?.kind !== "entity-attachment") {
      return { kind: "continue", input };
    }

    if (!context) {
      return {
        kind: "handled",
        result: { success: false, error: "Plugin context not initialized" },
      };
    }

    const generationData = documentGenerationJobSchema.parse({
      sourceEntityType: input.from.sourceEntityType,
      sourceEntityId: input.from.sourceEntityId,
      attachmentType: input.from.attachmentType,
      ...(input.title && { title: input.title }),
      ...(input.replace === true && { replace: true }),
      ...(input.targetEntityType && {
        targetEntityType: input.targetEntityType,
      }),
      ...(input.targetEntityId && { targetEntityId: input.targetEntityId }),
    });
    const dedupKey = await this.getDedupKey(generationData, context);
    const existing =
      generationData.replace === true
        ? undefined
        : await this.findExistingDocument(dedupKey, context);
    const documentId = existing?.id ?? getDocumentId(generationData, dedupKey);
    if (!existing) {
      await this.createPendingDocument(
        context,
        {
          id: documentId,
          title: generationData.title ?? documentId,
          filename: `${documentId}.pdf`,
          sourceEntityType: generationData.sourceEntityType,
          sourceEntityId: generationData.sourceEntityId,
          attachmentType: generationData.attachmentType,
          dedupKey,
        },
        executionContext,
      );
    }
    const jobId = await context.jobs.enqueue({
      type: "generate",
      data: { ...generationData, dedupKey, documentId },
      options: createAttributionJobOptions(executionContext),
    });

    const filename = `${documentId}.pdf`;
    return {
      kind: "handled",
      result: {
        success: true,
        data: {
          entityId: documentId,
          jobId,
          status: "generating",
          attachment: {
            mediaType: "application/pdf",
            url: `/api/chat/attachments/document?id=${encodeURIComponent(documentId)}`,
            downloadUrl: `/api/chat/attachments/document?id=${encodeURIComponent(documentId)}&download=1`,
            filename,
            source: {
              entityType: "document",
              entityId: documentId,
              attachmentType: generationData.attachmentType,
            },
          },
        },
      },
    };
  }

  private async promoteUpload(
    input: CreateInput,
    context: ServicePluginContext,
    executionContext: CreateExecutionContext,
  ): Promise<CreateInterceptionResult> {
    const uploadRef = input.from;
    if (uploadRef?.kind !== webChatUploadsScope.refKind) {
      return {
        kind: "handled",
        result: { success: false, error: "Unsupported upload ref kind" },
      };
    }

    let upload;
    try {
      upload = await context.uploads
        .scoped(webChatUploadsScope)
        .read(uploadRef.id);
    } catch {
      return {
        kind: "handled",
        result: { success: false, error: "Upload ref not found" },
      };
    }

    if (upload.record.mediaType !== "application/pdf") {
      return {
        kind: "handled",
        result: {
          success: false,
          error: "Only PDF uploads can be promoted to document entities",
        },
      };
    }

    const title = getUploadTitle(input, upload.record.filename);
    const id = slugify(title);
    if (!id) {
      return {
        kind: "handled",
        result: {
          success: false,
          error:
            "Could not derive a document id from the uploaded filename. Provide a title.",
        },
      };
    }

    const now = new Date().toISOString();
    const documentEntity = documentAdapter.createDocumentEntity({
      dataUrl: toDataUrl(upload.record.mediaType, upload.content),
      filename: upload.record.filename,
      title,
      status: "draft",
      sourceUploadId: uploadRef.id,
      sourceFilename: upload.record.filename,
      sourceMediaType: upload.record.mediaType,
      attachmentType: "uploaded",
      dedupKey: `upload:${uploadRef.kind}:${uploadRef.id}`,
    });
    const result = await context.entityService.createEntity({
      entity: {
        id,
        ...documentEntity,
        ...(input.visibility !== undefined
          ? { visibility: input.visibility }
          : {}),
        created: now,
        updated: now,
      },
      options: {
        deduplicateId: true,
        eventContext: {
          actor: executionContext.actor,
          interfaceType: executionContext.interfaceType,
        },
      },
    });

    return {
      kind: "handled",
      result: {
        success: true,
        data: {
          entityId: result.entityId,
          status: "created",
          attachment: buildUploadedDocumentAttachment({
            entityId: result.entityId,
            filename: upload.record.filename,
          }),
        },
      },
    };
  }

  private async createPendingDocument(
    context: ServicePluginContext,
    input: {
      id: string;
      title: string;
      filename: string;
      sourceEntityType: string;
      sourceEntityId: string;
      attachmentType: string;
      dedupKey: string;
    },
    executionContext: CreateExecutionContext,
  ): Promise<void> {
    const now = new Date().toISOString();
    const entityData = documentAdapter.createDocumentEntity({
      dataUrl: PENDING_PDF_DATA_URL,
      filename: input.filename,
      title: input.title,
      status: "pending",
      sourceEntityType: input.sourceEntityType,
      sourceEntityId: input.sourceEntityId,
      attachmentType: input.attachmentType,
      dedupKey: input.dedupKey,
    });

    await createPendingEntity({
      entityService: context.entityService,
      entity: {
        id: input.id,
        ...entityData,
        created: now,
        updated: now,
      },
      options: {
        eventContext: {
          actor: executionContext.actor,
          interfaceType: executionContext.interfaceType,
        },
      },
    });
  }

  private async getDedupKey(
    data: z.infer<typeof documentGenerationJobSchema>,
    context: ServicePluginContext,
  ): Promise<string> {
    if (data.dedupKey !== undefined) return data.dedupKey;
    if (data.renderUrl !== undefined) {
      return `${data.attachmentType}:${data.sourceEntityType}:${data.sourceEntityId}:${data.renderUrl}`;
    }

    const base = `${data.attachmentType}:${data.sourceEntityType}:${data.sourceEntityId}:resolved-attachment`;
    const source = await context.entityService.getEntity({
      entityType: data.sourceEntityType,
      id: data.sourceEntityId,
    });
    return source ? `${base}:${source.contentHash}` : base;
  }

  private async findExistingDocument(
    dedupKey: string,
    context: ServicePluginContext,
  ): Promise<DocumentEntity | undefined> {
    const documents = await context.entityService.listEntities(
      {
        entityType: "document",
        options: { filter: { metadata: { dedupKey } } },
      },
      documentSchema,
    );
    return documents[0];
  }

  protected override async getInstructions(): Promise<string> {
    return "Document entities store durable file artifacts such as PDFs. Uploaded PDFs are raw document files, not decks. Source-derived document artifacts are durable documents backed by registered attachment providers such as carousel or printable. Generate saved PDF artifacts through system_generate with source.kind attachment; preserve raw uploaded PDFs with system_create source.kind upload and transform preserve.";
  }

  protected override async getTools(): Promise<Tool[]> {
    return [];
  }
}

export function documentPlugin(): Plugin {
  return new DocumentPlugin();
}

export type { DocumentEntity };
